<template>
  <div class="reset-password-container">
    <div class="reset-password-wrapper">
      <!-- 左侧展示区 -->
      <div class="reset-password-left">
        <!-- 顶部横幅 -->
        <div class="top-banner">
          <div class="banner-content">
            <img src="@/assets/logo-xmyk.png" alt="系统Logo" class="banner-logo" />
            <h1 class="banner-title">数字化信息工作平台</h1>
          </div>
        </div>
        <div class="reset-password-image">
          <img src="@/assets/login-logo-2.jpg" alt="系统展示图" />
        </div>
      </div>

      <!-- 右侧找回密码区 -->
      <div class="reset-password-right">
        <el-card class="reset-password-box">
          <div class="header">
            <el-button 
              link 
              @click="goBack" 
              class="back-button"
            >
              <el-icon><ArrowLeft /></el-icon>
              返回登录
            </el-button>
            <h2>找回密码</h2>
          </div>

          <!-- 步骤指示器 -->
          <el-steps :active="currentStep" finish-status="success" class="steps">
            <el-step title="验证手机号" />
            <el-step title="输入验证码" />
            <el-step title="设置新密码" />
          </el-steps>

          <!-- 第一步：输入手机号 -->
          <div v-if="currentStep === 0" class="step-content">
            <el-form :model="phoneForm" :rules="phoneRules" ref="phoneFormRef">
              <el-form-item prop="phone">
                <el-input
                  v-model="phoneForm.phone"
                  placeholder="请输入手机号"
                  maxlength="11"
                >
                  <template #prefix>
                    <el-icon><Phone /></el-icon>
                  </template>
                </el-input>
              </el-form-item>
              <el-button
                type="primary"
                @click="handleSendCode"
                :loading="sendingCode"
                class="action-button"
              >
                发送验证码
              </el-button>
            </el-form>
          </div>

          <!-- 第二步：输入验证码 -->
          <div v-if="currentStep === 1" class="step-content">
            <el-form :model="codeForm" :rules="codeRules" ref="codeFormRef">
              <el-form-item prop="code">
                <el-input
                  v-model="codeForm.code"
                  placeholder="请输入验证码"
                  maxlength="6"
                >
                  <template #prefix>
                    <el-icon><Key /></el-icon>
                  </template>
                </el-input>
              </el-form-item>
              <div class="code-tip">
                <span>验证码已发送至 {{ phoneForm.phone }}</span>
                <el-button 
                  link 
                  @click="handleResendCode" 
                  :disabled="countdown > 0"
                  class="resend-button"
                >
                  {{ countdown > 0 ? `${countdown}s后重发` : '重新发送' }}
                </el-button>
              </div>
              <el-button
                type="primary"
                @click="handleVerifyCode"
                :loading="verifyingCode"
                class="action-button"
              >
                验证
              </el-button>
            </el-form>
          </div>

          <!-- 第三步：设置新密码 -->
          <div v-if="currentStep === 2" class="step-content">
            <el-form :model="passwordForm" :rules="passwordRules" ref="passwordFormRef">
              <el-form-item prop="new_password">
                <el-input
                  v-model="passwordForm.new_password"
                  type="password"
                  placeholder="请输入新密码"
                  show-password
                >
                  <template #prefix>
                    <el-icon><Lock /></el-icon>
                  </template>
                </el-input>
              </el-form-item>
              <el-form-item prop="confirm_password">
                <el-input
                  v-model="passwordForm.confirm_password"
                  type="password"
                  placeholder="请确认新密码"
                  show-password
                >
                  <template #prefix>
                    <el-icon><Lock /></el-icon>
                  </template>
                </el-input>
              </el-form-item>
              <el-button
                type="primary"
                @click="handleResetPassword"
                :loading="resettingPassword"
                class="action-button"
              >
                重置密码
              </el-button>
            </el-form>
          </div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  sendResetPasswordCode, 
  verifyResetPasswordCode, 
  resetPassword 
} from "@/api/sso/login";
import { 
  ArrowLeft, 
  Phone, 
  Key, 
  Lock 
} from "@element-plus/icons-vue";

export default {
  name: "ResetPassword",
  components: {
    ArrowLeft,
    Phone,
    Key,
    Lock,
  },
  data() {
    // 密码确认验证规则
    const validateConfirmPassword = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('请再次输入密码'));
      } else if (value !== this.passwordForm.new_password) {
        callback(new Error('两次输入密码不一致'));
      } else {
        callback();
      }
    };

    return {
      currentStep: 0,
      sendingCode: false,
      verifyingCode: false,
      resettingPassword: false,
      countdown: 0,
      countdownTimer: null,
      reset_token: '',
      
      phoneForm: {
        phone: '',
      },
      codeForm: {
        code: '',
      },
      passwordForm: {
        new_password: '',
        confirm_password: '',
      },
      
      phoneRules: {
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ]
      },
      codeRules: {
        code: [
          { required: true, message: '请输入验证码', trigger: 'blur' },
          { pattern: /^\d{6}$/, message: '验证码为6位数字', trigger: 'blur' }
        ]
      },
      passwordRules: {
        new_password: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        confirm_password: [
          { required: true, validator: validateConfirmPassword, trigger: 'blur' }
        ]
      }
    };
  },
  beforeUnmount() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  },
  methods: {
    goBack() {
      this.$router.push('/login');
    },
    
    // 发送验证码
    async handleSendCode() {
      try {
        await this.$refs.phoneFormRef.validate();
        this.sendingCode = true;
        
        const response = await sendResetPasswordCode({
          phone: this.phoneForm.phone
        });
        
        if (response.errcode === 0) {
          this.$message.success('验证码已发送');
          this.currentStep = 1;
          this.startCountdown();
        } else {
          this.$message.error(response.errmsg || '发送失败');
        }
      } catch (error) {
        console.error('发送验证码失败:', error);
        this.$message.error('发送验证码失败');
      } finally {
        this.sendingCode = false;
      }
    },
    
    // 验证验证码
    async handleVerifyCode() {
      try {
        await this.$refs.codeFormRef.validate();
        this.verifyingCode = true;
        
        const response = await verifyResetPasswordCode({
          phone: this.phoneForm.phone,
          code: this.codeForm.code
        });
        
        if (response.errcode === 0) {
          this.$message.success('验证码验证成功');
          this.currentStep = 2;
          this.reset_token = response.data.reset_token;
        } else {
          this.$message.error(response.errmsg || '验证码错误');
        }
      } catch (error) {
        console.error('验证码验证失败:', error);
        this.$message.error('验证码验证失败');
      } finally {
        this.verifyingCode = false;
      }
    },
    
    // 重置密码
    async handleResetPassword() {
      try {
        await this.$refs.passwordFormRef.validate();
        this.resettingPassword = true;
        
        const response = await resetPassword({
          phone: this.phoneForm.phone,
          reset_token: this.reset_token,
          new_password: this.passwordForm.new_password
        });
        
        if (response.errcode === 0) {
          this.$message.success('密码重置成功，请使用新密码登录');
          this.$router.push('/login');
        } else {
          this.$message.error(response.errmsg || '密码重置失败');
        }
      } catch (error) {
        console.error('密码重置失败:', error);
        this.$message.error('密码重置失败');
      } finally {
        this.resettingPassword = false;
      }
    },
    
    // 重新发送验证码
    async handleResendCode() {
      if (this.countdown > 0) return;
      
      try {
        this.sendingCode = true;
        
        const response = await sendResetPasswordCode({
          phone: this.phoneForm.phone
        });
        
        if (response.errcode === 0) {
          this.$message.success('验证码已重新发送');
          this.startCountdown();
        } else {
          this.$message.error(response.errmsg || '发送失败');
        }
      } catch (error) {
        console.error('重新发送验证码失败:', error);
        this.$message.error('重新发送验证码失败');
      } finally {
        this.sendingCode = false;
      }
    },
    
    // 开始倒计时
    startCountdown() {
      this.countdown = 90;
      this.countdownTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer);
        }
      }, 1000);
    }
  }
};
</script>

<style scoped>
.reset-password-container {
  min-height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  justify-content: center;
  align-items: center;
}

.reset-password-wrapper {
  display: flex;
  width: 100%;
  max-width: 1200px;
  min-height: 500px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.reset-password-left {
  flex: 1;
  background-color: #fff;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.top-banner {
  background: linear-gradient(135deg, #1e90ff, #70a1ff);
  padding: 12px 30px;
  color: #fff;
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.banner-logo {
  height: 40px;
  width: auto;
}

.banner-title {
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.reset-password-image {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.reset-password-image img {
  max-width: 90%;
  height: auto;
  max-height: 350px;
  border-radius: 4px;
}

.reset-password-right {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 30px;
  background-color: #fff;
  border-left: 1px solid #eee;
}

.reset-password-box {
  width: 100%;
  max-width: 400px;
  padding: 15px;
  box-shadow: none;
}

.header {
  position: relative;
  text-align: center;
  margin-bottom: 30px;
}

.back-button {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  color: #606266;
}

.header h2 {
  margin: 0;
  color: #303133;
}

.steps {
  margin-bottom: 30px;
}

.step-content {
  margin-top: 20px;
}

.action-button {
  width: 100%;
  height: 40px;
  font-size: 16px;
  margin-top: 20px;
}

.code-tip {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0;
  font-size: 14px;
  color: #606266;
}

.resend-button {
  padding: 0;
  font-size: 14px;
}

:deep(.el-input__wrapper) {
  box-shadow: 0 0 0 1px #dcdfe6 inset;
}

:deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #c0c4cc inset;
}

:deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #409eff inset;
}

:deep(.el-icon) {
  font-size: 16px;
  color: #909399;
}

/* 响应式布局调整 */
@media (max-width: 768px) {
  .reset-password-wrapper {
    flex-direction: column;
    margin: 20px;
    min-height: 0;
  }

  .reset-password-left {
    padding: 0;
  }

  .top-banner {
    padding: 10px 20px;
  }

  .banner-title {
    font-size: 20px;
  }

  .reset-password-image {
    padding: 20px;
  }

  .reset-password-image img {
    max-height: 200px;
  }

  .reset-password-right {
    padding: 20px;
    border-left: none;
    border-top: 1px solid #eee;
  }
}

@media (max-width: 480px) {
  .banner-content {
    flex-direction: column;
    gap: 10px;
    text-align: center;
  }

  .banner-logo {
    height: 30px;
  }

  .reset-password-wrapper {
    margin: 10px;
  }

  .reset-password-image {
    padding: 15px;
  }

  .reset-password-right {
    padding: 15px;
  }
}
</style> 